/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { config } from '../config/environment';

interface User {
  id: string | number; // 소셜 로그인 제공자의 id (provider_id)
  userId?: string; // users 테이블의 UUID id
  email: string;
  name: string;
  picture?: string;
  provider: string;
  profileComplete?: boolean; // 필수 필드 완성도
}

interface MedicationTime {
  time: string;
  period: string;
}

interface Medication {
  id: string;
  name: string;
  type: string;
  dosage: string;
  color?: string;
  shape?: string;
  imageUrl?: string;
  mealRelation?: string;
  doseAmount?: number;
  doseUnit?: string;
  scheduleId?: string;
  reminderId?: string;
  status?: 'taken' | 'missed' | 'skipped' | 'pending';
  scheduledTimes: MedicationTime[];
  takenCount: number;
}

interface TodayMedsSummary {
  date: string;
  userId: string;
  summary: {
    totalScheduled: number;
    totalTaken: number;
    completionRate: number;
  };
  medications: Medication[];
}

interface BloodPressureData {
  id: string;
  systolic: number;
  diastolic: number;
  pulse?: number;
  measurement_time?: string;
}

interface BloodGlucoseData {
  id: string;
  glucose_level: number;
  meal_type?: string;
  meal_relation?: string;
  measurement_time?: string;
}

interface StepsData {
  id: string;
  count?: number;
  distance?: number;
  calories?: number;
  start_time?: string;
  end_time?: string;
  step_count?: number;
  step_date?: string;
  calories_burned?: number;
  notes?: string;
}

interface TodayHealthData {
  date: string;
  bloodPressure: BloodPressureData[] | null;
  bloodGlucose: BloodGlucoseData[] | null;
  steps: StepsData | null;
  nutrition?: {
    totalCalories: number;
    protein: number;
    carbohydrates: number;
    fat: number;
    fiber: number;
  } | null;
}

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;
  setIsLoggedIn: (loggedIn: boolean) => void;
  setUser: (user: User | null) => void;
  logout: () => void;
  todayMeds: TodayMedsSummary | null;
  setTodayMeds: (meds: TodayMedsSummary | null) => void;
  loadTodayMeds: () => Promise<void>;
  refreshTodayMeds: () => Promise<void>;
  loadMedsByDate: (dateStr: string) => Promise<TodayMedsSummary | null>;
  loadMedicationList: (limit?: number, offset?: number) => Promise<any>;
  loadMedicationRecords: (medicationId: string) => Promise<any>;
  todayHealth: TodayHealthData | null;
  setTodayHealth: (health: TodayHealthData | null) => void;
  loadTodayHealth: () => Promise<void>;
  loadHealthByDate: (dateStr: string) => Promise<TodayHealthData | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [todayMeds, setTodayMeds] = useState<TodayMedsSummary | null>(null);
  const [todayHealth, setTodayHealth] = useState<TodayHealthData | null>(null);
  
  // 초기 데이터 로드 플래그 (정확히 1회만 실행되도록)
  const isInitialLoadRef = useRef(false);

  const loadTodayMeds = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('[loadTodayMeds] No token available');
        return;
      }

      // localStorage에서 먼저 확인
      const cachedMeds = localStorage.getItem('todayMeds');
      if (cachedMeds) {
        try {
          const parsedMeds = JSON.parse(cachedMeds);
          console.log('[loadTodayMeds] Loaded from cache:', parsedMeds);
          setTodayMeds(parsedMeds);
          return; // 캐시가 있으면 API 호출하지 않음
        } catch (e) {
          console.warn('[loadTodayMeds] Failed to parse cached meds');
        }
      }

      // 캐시 없으면 API 호출
      const apiUrl = config.apiUrl;
      console.log('[loadTodayMeds] No cache found, calling API:', `${apiUrl}/meds/today`);
      
      const response = await fetch(`${apiUrl}/meds/today`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[loadTodayMeds] API error:', response.status, errorText);
        return;
      }

      const data = await response.json();
      console.log('[loadTodayMeds] Success:', data);
      if (data.success) {
        setTodayMeds(data);
        // localStorage에 저장
        localStorage.setItem('todayMeds', JSON.stringify(data));
        console.log('[loadTodayMeds] Cached to localStorage');
      }
    } catch (error) {
      console.error('[loadTodayMeds] Network/parse error:', error);
    }
  }, []);

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('user');

        if (token && userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setIsLoggedIn(true);
          setShowLoginModal(false);
        } else {
          setIsLoggedIn(false);
          setShowLoginModal(true);
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        setIsLoggedIn(false);
        setShowLoginModal(true);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // 사용자 로그인 후 자동으로 대시보드 데이터 로드 (프로필 + 복약 + 건강 한 번에)
  // ⭐ 핵심: useRef 플래그로 정확히 1회만 실행하되, 조건이 맞을 때까지 기다림
  // dependency array에 isInitialized, isLoggedIn 포함 → 조건이 충족될 때까지 재시도
  useEffect(() => {
    console.log(
      '%c[AuthContext] Dashboard Init Effect',
      'color: #ff00ff; font-weight: bold; font-size: 14px',
      {
        isInitialLoadRef: isInitialLoadRef.current,
        isInitialized,
        isLoggedIn,
        userId: user?.id,
      }
    );

    // 조건이 안 맞으면 skip
    if (!isInitialized || !isLoggedIn || !user?.id) {
      console.log(
        '%c[AuthContext] ⏭️  Not ready yet',
        'color: #ff9900; font-weight: bold',
        { isInitialized, isLoggedIn, userId: user?.id }
      );
      return;
    }

    // 이미 한 번 실행했으면 skip
    if (isInitialLoadRef.current) {
      console.log(
        '%c[AuthContext] ⏭️  Already executed',
        'color: #ff9900; font-weight: bold'
      );
      return;
    }

    // 플래그 설정 (이제 정확히 1회만 실행됨)
    isInitialLoadRef.current = true;

    console.log(
      '%c[AuthContext] 🚀 Starting Dashboard Init',
      'color: #00cc33; font-weight: bold; font-size: 14px',
      { userId: user?.id }
    );
    
    let isMounted = true;
    const abortController = new AbortController();
    
    const loadData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token || abortController.signal.aborted || !isMounted) {
          console.warn('[AuthContext loadData] No token or aborted or unmounted');
          return;
        }

        const apiUrl = config.apiUrl;
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // 통합 엔드포인트로 프로필 + 복약 + 건강 데이터를 한 번에 로드
        console.log(
          '%c[AuthContext] 🔴 API CALL',
          'color: #cc0000; font-weight: bold',
          `${apiUrl}/dashboard/init`
        );
        const response = await fetch(`${apiUrl}/dashboard/init`, 
          { headers, signal: abortController.signal }
        );

        if (abortController.signal.aborted || !isMounted) {
          console.log('[AuthContext] 🟡 Dashboard init request was cancelled');
          return;
        }

        if (!response.ok) {
          throw new Error(`Dashboard init failed: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success && !abortController.signal.aborted && isMounted) {
          // 프로필 정보 처리
          if (data.profile) {
            const updatedUser: User = {
              id: data.profile.id,
              email: data.profile.email,
              name: data.profile.name,
              picture: data.profile.picture,
              provider: data.profile.provider,
              profileComplete: data.profile.profileComplete
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            console.log('[AuthContext] ✅ Profile loaded');
          }

          // 복약 데이터 처리
          if (data.meds) {
            setTodayMeds(data.meds);
            localStorage.setItem('todayMeds', JSON.stringify(data.meds));
            console.log('[AuthContext] ✅ Meds data loaded');
          }

          // 건강 데이터 처리
          if (data.health) {
            setTodayHealth(data.health);
            localStorage.setItem('todayHealth', JSON.stringify(data.health));
            console.log('[AuthContext] ✅ Health data loaded');
          }

          console.log(
            '%c[AuthContext] ✅ Dashboard Init Complete',
            'color: #00cc33; font-weight: bold; font-size: 14px'
          );
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('[AuthContext] Dashboard init aborted');
          return;
        }
        console.error('[AuthContext] ❌ Error loading dashboard init data:', error);
        // 폴백: 기존 개별 엔드포인트 사용
        console.log('[AuthContext] Falling back to individual endpoint calls');
        await loadDataFallback(abortController.signal);
      }
    };

    // 폴백 함수: 통합 엔드포인트 실패 시 개별 요청
    const loadDataFallback = async (signal: AbortSignal) => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token || signal.aborted) return;

        const apiUrl = config.apiUrl;
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const [profileResponse, medsResponse, healthResponse] = await Promise.all([
          fetch(`${apiUrl}/user/profile`, { headers, signal }),
          fetch(`${apiUrl}/meds/today`, { headers, signal }),
          fetch(`${apiUrl}/health/today`, { headers, signal })
        ]);

        if (signal.aborted) return;

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          if (profileData.success && profileData.user) {
            const updatedUser: User = {
              id: profileData.user.id,
              email: profileData.user.email,
              name: profileData.user.name,
              picture: profileData.user.picture,
              provider: profileData.user.provider,
              profileComplete: profileData.user.profileComplete
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        }

        if (medsResponse.ok) {
          const medsData = await medsResponse.json();
          if (medsData.success) {
            setTodayMeds(medsData);
            localStorage.setItem('todayMeds', JSON.stringify(medsData));
          }
        }

        if (healthResponse.ok) {
          const healthData = await healthResponse.json();
          setTodayHealth(healthData);
          localStorage.setItem('todayHealth', JSON.stringify(healthData));
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('[AuthContext] Fallback request aborted');
          return;
        }
        console.error('[AuthContext] Fallback data load failed:', error);
      }
    };
    
    loadData();

    // cleanup: 이전 요청 취소 및 마운트 상태 해제
    return () => {
      console.log('[AuthContext] Cleaning up dashboard init effect');
      isMounted = false;
      abortController.abort();
    };
  }, [isInitialized, isLoggedIn]);

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('todayMeds');
    localStorage.removeItem('todayHealth');
    isInitialLoadRef.current = false;
    setIsLoggedIn(false);
    setUser(null);
    setShowLoginModal(true);
    setTodayMeds(null);
    setTodayHealth(null);
  };

  // 복약 데이터 새로고침 (수동)
  const refreshTodayMeds = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token || !user) return;

      const apiUrl = config.apiUrl;
      
      const response = await fetch(`${apiUrl}/meds/today`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.warn(`[refreshTodayMeds] API error: ${response.status}`);
        return;
      }

      const data = await response.json();
      if (data.success) {
        const medsData = {
          ...data,
          userId: user.id
        };
        setTodayMeds(medsData);
        localStorage.setItem('todayMeds', JSON.stringify(medsData));
      }
    } catch (error) {
      console.warn('[refreshTodayMeds] Failed to refresh meds');
    }
  }, [user]);

  // 건강 데이터 로드 (혈압, 혈당, 걸음)
  const loadTodayHealth = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('[loadTodayHealth] No token available');
        return;
      }

      const apiUrl = config.apiUrl;
      console.log('[loadTodayHealth] Calling API:', `${apiUrl}/health/today`);
      
      const response = await fetch(`${apiUrl}/health/today`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[loadTodayHealth] API error:', response.status, errorText);
        return;
      }

      const data = await response.json();
      console.log('[loadTodayHealth] Success:', data);
      if (data) {
        setTodayHealth(data);
        localStorage.setItem('todayHealth', JSON.stringify(data));
      }
    } catch (error) {
      console.error('[loadTodayHealth] Network/parse error:', error);
    }
  }, []);

  // 특정 날짜의 건강 데이터 조회
  const loadHealthByDate = useCallback(async (dateStr: string): Promise<TodayHealthData | null> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('[loadHealthByDate] No token available');
        return null;
      }

      const apiUrl = config.apiUrl;
      console.log('[loadHealthByDate] Calling API:', `${apiUrl}/health/date/${dateStr}`);

      const response = await fetch(`${apiUrl}/health/date/${dateStr}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[loadHealthByDate] API error:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('[loadHealthByDate] Success:', data);
      return data;
    } catch (error) {
      console.error('[loadHealthByDate] Network/parse error:', error);
      return null;
    }
  }, []);

  // 특정 날짜의 복약 데이터 조회
  const loadMedsByDate = useCallback(async (dateStr: string): Promise<TodayMedsSummary | null> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('[loadMedsByDate] No token available');
        return null;
      }

      const apiUrl = config.apiUrl;
      console.log('[loadMedsByDate] Calling API:', `${apiUrl}/meds/date/${dateStr}`);

      const response = await fetch(`${apiUrl}/meds/date/${dateStr}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[loadMedsByDate] API error:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('[loadMedsByDate] Success:', data);
      return data;
    } catch (error) {
      console.error('[loadMedsByDate] Network/parse error:', error);
      return null;
    }
  }, []);

  // 약물 목록 조회 (페이지네이션)
  const loadMedicationList = useCallback(async (limit: number = 10, offset: number = 0): Promise<any> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('[loadMedicationList] No token available');
        return null;
      }

      const apiUrl = config.apiUrl;
      console.log('[loadMedicationList] Calling API:', `${apiUrl}/meds/list?limit=${limit}&offset=${offset}`);

      const response = await fetch(`${apiUrl}/meds/list?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[loadMedicationList] API error:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('[loadMedicationList] Success:', data);
      return data;
    } catch (error) {
      console.error('[loadMedicationList] Network/parse error:', error);
      return null;
    }
  }, []);

  // 약물별 복용 기록 조회
  const loadMedicationRecords = useCallback(async (medicationId: string): Promise<any> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('[loadMedicationRecords] No token available');
        return null;
      }

      const apiUrl = config.apiUrl;
      console.log('[loadMedicationRecords] Calling API:', `${apiUrl}/meds/${medicationId}/records`);

      const response = await fetch(`${apiUrl}/meds/${medicationId}/records`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[loadMedicationRecords] API error:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('[loadMedicationRecords] Success:', data);
      return data;
    } catch (error) {
      console.error('[loadMedicationRecords] Network/parse error:', error);
      return null;
    }
  }, []);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        showLoginModal,
        setShowLoginModal,
        setIsLoggedIn,
        setUser,
        logout,
        todayMeds,
        setTodayMeds,
        loadTodayMeds,
        refreshTodayMeds,
        loadMedsByDate,
        loadMedicationList,
        loadMedicationRecords,
        todayHealth,
        setTodayHealth,
        loadTodayHealth,
        loadHealthByDate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
